import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import Config from "../utils/Config";
import "./DashboardSupportTickets.css";

export async function loadDashboardTickets({ token, signal, request = fetch }) {
  if (!token) throw new Error("Please sign in again to view support tickets.");
  const url = Config.API.API_BASE_URL + "/api/support/my-requests";
  const options = { headers: { Authorization: "Bearer " + token }, signal, cache: "no-store" };
  const response = await request(url + "?page=1&status=OPEN", options);
  if (!response.ok) throw new Error("Unable to load support tickets. Please try again.");
  const data = await response.json();
  if (!Number.isSafeInteger(data.total) || data.total < 0 || !Array.isArray(data.requests) || data.requests.some(t => !t || typeof t._id !== "string" || typeof t.subject !== "string" || t.status !== "OPEN")) throw new Error("Unable to read support tickets.");
  const tickets = await Promise.all(data.requests.slice(0, 3).map(async ticket => {
    try {
      const detail = await request(url + "/" + encodeURIComponent(ticket._id), options);
      if (!detail.ok) return { ...ticket, administratorReplied: false };
      const result = await detail.json();
      const messages = result.ticket?._id === ticket._id && Array.isArray(result.ticket.messages) ? result.ticket.messages : [];
      return { ...ticket, administratorReplied: messages[messages.length - 1]?.authorRole === "ADMIN" };
    } catch (error) { if (signal?.aborted) throw error; return { ...ticket, administratorReplied: false }; }
  }));
  return { total: data.total, tickets };
}

export function SupportTicketsContent({ result, loading, error, onRetry, onOpenTicket }) {
  return <article className="do-panel do-support-tickets" aria-label="Support tickets">
    <header><h2>Support Tickets</h2>{onOpenTicket && <button type="button" className="do-activity-link" onClick={() => onOpenTicket(null)}>View Support Inbox</button>}</header>
    {loading ? <p role="status">Loading support tickets…</p> : error ? <div role="alert"><p>{error}</p><button type="button" className="do-activity-back" onClick={onRetry}>Try Again</button></div> : result && <>
      <p>{result.total === 0 ? "No open support tickets." : result.total + " open support ticket" + (result.total === 1 ? "" : "s")}</p>
      {!!result.tickets.length && <ul>{result.tickets.map(ticket => <li key={ticket._id}><button type="button" onClick={() => onOpenTicket?.(ticket._id)}><strong>{ticket.subject}</strong><span>Open{ticket.administratorReplied ? " · Administrator replied — view conversation" : ""}</span></button></li>)}</ul>}
    </>}
  </article>;
}

export default function DashboardSupportTickets({ onOpenTicket }) {
  const { user } = useContext(AuthContext);
  const userId = user?.id || user?._id;
  return userId ? <AccountSupportTickets key={String(userId)} onOpenTicket={onOpenTicket} /> : null;
}
function AccountSupportTickets({ onOpenTicket }) {
  const [result, setResult] = useState(null), [loading, setLoading] = useState(true), [error, setError] = useState(""), [reload, setReload] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setResult(null); setError("");
    loadDashboardTickets({ token: localStorage.getItem("authToken") || sessionStorage.getItem("authToken"), signal: controller.signal })
      .then(data => { if (!controller.signal.aborted) setResult(data); })
      .catch(failure => { if (!controller.signal.aborted) setError(failure.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [reload]);
  return <SupportTicketsContent result={result} loading={loading} error={error} onRetry={() => setReload(v => v + 1)} onOpenTicket={onOpenTicket} />;
}
